(function () {
  'use strict';

  /* ─── Constants ─────────────────────────────────────────── */
  var API_BASE = 'https://shopify-custom-backend-api-to-get-p.vercel.app/api/products/';
  var FETCH_TIMEOUT_MS = 8000;

  /* ─── State ─────────────────────────────────────────────── */
  var state = {
    productId: null,
    currentColor: null,
    locationMap: {},
    quantities: {},
    isFetching: false,
    notifyVariantLabel: '',
    layout: 'card_grid',
    colorOptions: [],
    showAvailableOnly: false
  };

  /* ─── DOM refs ────────────────────────────────────────────── */
  var root, loadingEl, tmLoadingEl, errorEl, contentEl, atcBtn, atcBtnText, atcSpinner;
  var scOverlay, nmOverlay, nmTitle, nmDesc, nmForm, nmSuccess, nmEmailInput;
  var toastEl;
  var colorCountEl, colorNameEl, stockOnlyCheckbox;

  /* ─────────────────────────────────────────────────────────── *
   *  INIT
   * ─────────────────────────────────────────────────────────── */
  function init() {
    root = document.getElementById('wh-inventory-root');
    if (!root) return;

    // Move popup overlays to <body> so they are never nested inside
    // the product form — prevents Enter in email input submitting the cart.
    ['wh-nm-overlay', 'wh-sc-overlay'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el && el.parentNode !== document.body) document.body.appendChild(el);
    });

    state.productId = root.dataset.productId;
    state.currentColor = root.dataset.initialColor || '';
    state.layout = root.dataset.layout || 'card_grid';
    state.colorOptions = parseColorOptions();

    cacheDOM();
    bindColorListener();
    bindAtcButton();
    bindSizeChart();
    bindNotifyPopup();
    bindCardInputs();
    bindTableInputs();
    bindSwatchClicks();
    bindSwatchHover();
    bindStockOnlyCheckbox();
    bindVariantChangeListener();
    createToast();

    if (state.productId) {
      fetchInventory(state.productId, state.currentColor);
    }
  }

  function cacheDOM() {
    loadingEl = document.getElementById('wh-loading');
    tmLoadingEl = document.getElementById('wh-tm-loading');
    errorEl = document.getElementById('wh-error');
    contentEl = document.getElementById('wh-content');
    atcBtn = document.getElementById('wh-atc-btn');
    atcBtnText = document.getElementById('wh-atc-btn-text');
    atcSpinner = document.getElementById('wh-atc-spinner');
    scOverlay = document.getElementById('wh-sc-overlay');
    nmOverlay = document.getElementById('wh-nm-overlay');
    nmTitle = document.getElementById('wh-nm-title');
    nmDesc = document.getElementById('wh-nm-desc');
    nmForm = document.getElementById('wh-nm-form');
    nmSuccess = document.getElementById('wh-nm-success');
    nmEmailInput = document.getElementById('wh-nm-email');
    colorCountEl = document.getElementById('wh-color-count');
    colorNameEl = document.getElementById('wh-current-color-name');
    stockOnlyCheckbox = document.getElementById('wh-stock-only-checkbox');
  }

  /* ─────────────────────────────────────────────────────────── *
   *  COLOR CHANGE LISTENER
   * ─────────────────────────────────────────────────────────── */
  function bindColorListener() {
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (
        input.tagName === 'INPUT' &&
        input.type === 'radio' &&
        (input.name === 'Color' || input.name === 'color' || input.name === 'Colour' ||
          (input.closest && input.closest('[data-option-name="Color"]')))
      ) {
        var newColor = input.value || input.dataset.value || input.getAttribute('value') || '';
        if (newColor && newColor !== state.currentColor) {
          state.currentColor = newColor;
          syncColorDropdown(newColor);
          updateSwatchActive(newColor);
          fetchInventory(state.productId, state.currentColor);
        }
      }
    });

    // Watch for label-click swatches that update a hidden input via attribute mutation
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'value') {
          var el = m.target;
          if (el.name === 'Color' || el.name === 'color' || el.name === 'Colour') {
            var newColor = el.value;
            if (newColor && newColor !== state.currentColor) {
              state.currentColor = newColor;
              syncColorDropdown(newColor);
              updateSwatchActive(newColor);
              fetchInventory(state.productId, state.currentColor);
            }
          }
        }
      });
    });

    var productSingle = document.getElementById('product-single');
    if (productSingle) {
      observer.observe(productSingle, { subtree: true, attributes: true, attributeFilter: ['value'] });
    }
  }

  /* ─────────────────────────────────────────────────────────── *
   *  API FETCH
   * ─────────────────────────────────────────────────────────── */
  function fetchInventory(productId, colorName) {
    if (state.isFetching) return;

    var isAvailable = true;
    state.colorOptions.forEach(function (opt) {
      if (opt.name === colorName) isAvailable = opt.available !== false;
    });

    if (!isAvailable) {
      if (state.layout === 'image_swatch_table' || state.layout === 'image_swatch_table_readonly') {
        renderColorSwatches();
      }
      renderOutOfStock();
      setState('content');
      state.isFetching = false;
      return;
    }

    state.isFetching = true;

    setState('loading');

    var url = API_BASE + productId;
    if (colorName) {
      url += '?colorName=' + encodeURIComponent(colorName);
    }

    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);

    fetch(url, { signal: controller.signal })
      .then(function (res) {
        clearTimeout(timeoutId);
        if (!res.ok) {
          return res.json().then(function (body) {
            throw new Error(body.message || 'Server error (' + res.status + ')');
          });
        }
        return res.json();
      })
      .then(function (json) {
        if (!json.success) {
          throw new Error(json.message || 'Could not load inventory data.');
        }
        var variants = (json.data && json.data.variants) ? json.data.variants : [];
        if (!variants.length) {
          throw new Error('No inventory data found for this colour. Please try another.');
        }
        state.locationMap = transformVariants(variants);
        window._whVariants = variants;
        window._whLocationMap = state.locationMap;
        state.quantities = {};
        render();
        var po = document.querySelector('product-options');
        if (po && typeof po.updatePossibleVariants === 'function' && po.jsonProduct) {
          po.updatePossibleVariants({ json: po.jsonProduct });
        }
        setState('content');
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        var msg;
        if (err.name === 'AbortError') {
          msg = 'The request timed out. Please check your connection and try again.';
        } else if (err.message) {
          msg = err.message;
        } else {
          msg = 'Something went wrong loading inventory. Please try again.';
        }
        showError(msg);
        setState('error');
      })
      .finally(function () {
        state.isFetching = false;
      });
  }

  /* ─────────────────────────────────────────────────────────── *
   *  DATA TRANSFORM
   *  API: data.variants[] each has inventoryItem.inventoryLevels.edges[]
   *       → node.location.name, node.quantities[0].quantity
   *       selectedOptions[1].value = size
   * ─────────────────────────────────────────────────────────── */
  function transformVariants(variants) {
    var map = {};

    variants.forEach(function (variant) {
      // Extract size from selectedOptions
      var sizeOption = null;
      if (variant.selectedOptions) {
        variant.selectedOptions.forEach(function (opt) {
          if (opt.name === 'Size' || opt.name === 'size') sizeOption = opt.value;
        });
      }
      var size = sizeOption || variant.title;

      // Numeric variant ID from GID: "gid://shopify/ProductVariant/52029390553401"
      var gid = variant.id || '';
      var numericId = gid.split('/').pop();

      // Price formatted
      var price = variant.price ? '$' + parseFloat(variant.price).toFixed(2) : '';

      // Each inventory level node
      var edges = [];
      try { edges = variant.inventoryItem.inventoryLevels.edges; } catch (e) { }

      edges.forEach(function (edge) {
        var node = edge.node;
        var locationName = (node.location && node.location.name) ? node.location.name : 'Unknown';
        // Capture numeric location ID from GID: "gid://shopify/Location/112234791225" → "112234791225"
        var locationGid = (node.location && node.location.id) ? node.location.id : '';
        var locationNumeric = locationGid.split('/').pop();
        var qty = 0;
        if (node.quantities && node.quantities.length) {
          qty = node.quantities[0].quantity || 0;
        }

        // Map value is now { id: numericLocationId, items: [] } instead of a bare array
        if (!map[locationName]) map[locationName] = { id: locationNumeric, items: [] };

        map[locationName].items.push({
          size: size,
          price: price,
          qty: qty,
          variantId: gid,
          numericId: numericId
        });
      });
    });

    return map;
  }

  /* ─────────────────────────────────────────────────────────── *
   *  RENDER DISPATCHER
   * ─────────────────────────────────────────────────────────── */
  function render() {
    if (state.layout === 'table_matrix') {
      renderTable();
    } else if (state.layout === 'image_swatch_table' || state.layout === 'image_swatch_table_readonly') {
      renderColorSwatches();
      renderTable();
    } else {
      renderCards();
    }
  }

  /* ─────────────────────────────────────────────────────────── *
   *  RENDER — CARD GRID (existing layout)
   * ─────────────────────────────────────────────────────────── */
  function renderCards() {
    if (!contentEl) return;
    preserveReadonlySelectors();

    var html = '';
    var locationNames = Object.keys(state.locationMap);

    locationNames.forEach(function (locName) {
      var items = state.locationMap[locName].items;
      var allSoldOut = items.every(function (i) { return i.qty <= 0; });

      html += '<div class="wh-location-block">';
      html += '<div class="wh-loc-header">';
      html += '<span class="wh-loc-dot' + (allSoldOut ? ' is-sold-out' : '') + '"></span>';
      html += '<span class="wh-loc-name">' + escHtml(locName) + '</span>';
      if (allSoldOut) {
        html += '<span style="margin-left:auto;font-size:11px;color:#dc2626;font-weight:600;">All sizes sold out</span>';
      }
      html += '</div>';

      html += '<div class="wh-sizes-grid">';
      items.forEach(function (item) {
        var soldOut = item.qty <= 0;
        var qKey = locName + '|||' + item.size;
        var currentQty = state.quantities[qKey] || 0;

        html += '<div class="wh-size-card' + (soldOut ? ' is-sold-out' : '') + '"'
          + ' data-loc="' + escAttr(locName) + '"'
          + ' data-size="' + escAttr(item.size) + '"'
          + ' data-variant-id="' + escAttr(item.numericId) + '"'
          + ' data-gid="' + escAttr(item.variantId) + '"'
          + ' data-max="' + item.qty + '"'
          + ' data-price="' + escAttr(item.price) + '"'
          + '>';

        html += '<span class="wh-size-label">' + escHtml(item.size) + '</span>';
        html += '<span class="wh-size-price">' + escHtml(item.price) + '</span>';

        if (soldOut) {
          html += '<span class="wh-size-avail">Sold Out</span>';
          html += '<button type="button" class="wh-notify-btn"'
            + ' data-notify-loc="' + escAttr(locName) + '"'
            + ' data-notify-size="' + escAttr(item.size) + '"'
            + '>Notify Me</button>';
        } else {
          html += '<span class="wh-size-avail">' + item.qty + ' avail.</span>';
          html += '<div class="wh-stepper">';
          html += '<button type="button" class="wh-stepper-btn wh-stepper-minus" aria-label="Decrease" data-action="minus"'
            + (currentQty <= 0 ? ' disabled' : '') + '>−</button>';
          html += '<span class="wh-stepper-val">' + currentQty + '</span>';
          html += '<button type="button" class="wh-stepper-btn wh-stepper-plus" aria-label="Increase" data-action="plus"'
            + (currentQty >= item.qty ? ' disabled' : '') + '>+</button>';
          html += '</div>';
        }

        html += '</div>'; // .wh-size-card
      });
      html += '</div>'; // .wh-sizes-grid
      html += '</div>'; // .wh-location-block
    });

    contentEl.innerHTML = html;
    updateAtcButton();
  }

  /* ─────────────────────────────────────────────────────────── *
   *  CARD INPUTS (bound once from init)
   * ─────────────────────────────────────────────────────────── */
  function bindCardInputs() {
    if (!contentEl) return;

    contentEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.wh-stepper-btn');
      if (!btn) return;

      // Only block propagation for stepper clicks — prevents theme form handlers
      // from firing while still allowing the notify-button listeners to work.
      e.preventDefault();
      e.stopPropagation();

      var card = btn.closest('.wh-size-card');
      if (!card) return;

      var locName = card.dataset.loc;
      var size = card.dataset.size;
      var maxQty = parseInt(card.dataset.max, 10) || 0;
      var qKey = locName + '|||' + size;
      var current = state.quantities[qKey] || 0;
      var action = btn.dataset.action;

      if (action === 'plus') { if (current < maxQty) current++; }
      else if (action === 'minus') { if (current > 0) current--; }

      state.quantities[qKey] = current;

      var valEl = card.querySelector('.wh-stepper-val');
      var minusBtn = card.querySelector('.wh-stepper-minus');
      var plusBtn = card.querySelector('.wh-stepper-plus');
      if (valEl) valEl.textContent = current;
      if (minusBtn) minusBtn.disabled = (current <= 0);
      if (plusBtn) plusBtn.disabled = (current >= maxQty);

      updateAtcButton();
    });

    contentEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.wh-notify-btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      openNotifyPopup(btn.dataset.notifyLoc, btn.dataset.notifySize);
    });
  }

  /* ─────────────────────────────────────────────────────────── *
   *  ATC BUTTON
   * ─────────────────────────────────────────────────────────── */
  function updateAtcButton() {
    if (!atcBtn || !atcBtnText) return;

    if (state.layout === 'image_swatch_table_readonly') {
      var input = document.querySelector('[data-js-product-variant-input]');
      if (!input || !input.value) {
        atcBtn.disabled = true;
        atcBtnText.textContent = 'SELECT SIZE';
        return;
      }
      var variantId = input.value;
      var totalStock = 0;
      var hasLocationData = Object.keys(state.locationMap).length > 0;
      Object.keys(state.locationMap).forEach(function(locName) {
        (state.locationMap[locName].items || []).forEach(function(item) {
          if (String(item.numericId) === String(variantId)) totalStock += item.qty;
        });
      });

      // ponytail: same fallback as updateReadonlyStockDisplay — ID may be stale/wrong-colour
      if (hasLocationData && totalStock === 0) {
        var activeEl = document.querySelector(
          '.cms-option-item[data-position="2"] .product-options__value.active input'
        );
        var activeSize = activeEl ? activeEl.value : '';
        if (!activeSize) {
          var lbl = document.querySelector('.cms-option-item[data-position="2"] .label-selected');
          if (lbl) activeSize = lbl.textContent.trim();
        }
        if (activeSize) {
          Object.keys(state.locationMap).forEach(function(locName) {
            (state.locationMap[locName].items || []).forEach(function(item) {
              if (item.size === activeSize) totalStock += item.qty;
            });
          });
        }
      }

      var variant = getVariantById(variantId);
      var isAvailable = false;
      if (hasLocationData) {
        isAvailable = (totalStock > 0);
      } else if (variant && typeof variant.available === 'boolean') {
        isAvailable = variant.available;
      } else {
        isAvailable = true;
      }

      if (isAvailable) {
        atcBtn.disabled = false;
        atcBtnText.textContent = 'ADD TO CART';
      } else {
        atcBtn.disabled = true;
        atcBtnText.textContent = 'SOLD OUT';
      }
      return;
    }

    var total = 0;
    Object.keys(state.quantities).forEach(function (k) {
      total += state.quantities[k] || 0;
    });

    if (total > 0) {
      atcBtn.disabled = false;
      atcBtnText.textContent = 'ADD ' + total + ' ITEM' + (total !== 1 ? 'S' : '') + ' TO CART';
    } else {
      atcBtn.disabled = true;
      atcBtnText.textContent = 'SELECT SIZES ABOVE';
    }
  }

  function bindAtcButton() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest('#wh-atc-btn')) return;
      addToCart();
    });
  }

  /* ─────────────────────────────────────────────────────────── *
   *  ADD TO CART
   * ─────────────────────────────────────────────────────────── */
  function addToCart() {
    var items = [];

    if (state.layout === 'image_swatch_table_readonly') {
      var input = document.querySelector('[data-js-product-variant-input]');
      var qtyInput = document.querySelector('.wh-readonly-interactive-selectors input.js_qty');
      if (input && input.value) {
        var variantId = parseInt(input.value, 10);
        var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
        if (qty > 0) {
          items.push({
            id: variantId,
            quantity: qty
          });
        }
      }
    } else {
      Object.keys(state.quantities).forEach(function (qKey) {
        var qty = state.quantities[qKey] || 0;
        if (qty <= 0) return;

        var parts = qKey.split('|||');
        var locName = parts[0];
        var size = parts[1];
        var locEntry = state.locationMap[locName] || { id: '', items: [] };
        var locItems = locEntry.items;
        var found = null;

        locItems.forEach(function (i) { if (i.size === size) found = i; });
        if (!found || !found.numericId) return;

        items.push({
          id: parseInt(found.numericId, 10),
          quantity: qty,
          properties: {
            'Warehouse Orders': locName + ': ' + qty + ' × ' + size,
            'warehouse': locEntry.id
          }
        });
      });
    }

    if (!items.length) return;

    setAtcLoading(true);

    fetch(window.Shopify ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (body) {
            throw new Error(body.description || body.message || 'Could not add items to cart.');
          });
        }
        return res.json();
      })
      .then(function (response) {
        // Reset quantities
        state.quantities = {};
        render();

        // Step 1: Fetch fresh cart data from Shopify to update mini-cart HTML
        fetch('/cart?view=minicart')
          .then(function (r) { return r.text(); })
          .then(function (html) {
            // Dispatch drawer:Minicart so the theme refreshes the mini-cart panel
            document.dispatchEvent(new CustomEvent('drawer:Minicart', {
              detail: { response: { sections: { minicart: html } } }
            }));

            // Also fire ajax:addToCart so cart count badge updates
            document.body.dispatchEvent(new CustomEvent('ajax:addToCart', {
              bubbles: true,
              detail: { form: null, response: { items: response.items || [] } }
            }));

            // Step 2: Open cart drawer AFTER mini-cart content is ready
            var cartTrigger = document.querySelector('.push_side[data-id="#js_cart_popup"]');
            if (cartTrigger && !document.documentElement.classList.contains('pside_opened')) {
              setTimeout(function () { cartTrigger.click(); }, 120);
            }
          })
          .catch(function () {
            // Fallback: just open the drawer even if refresh failed
            var cartTrigger = document.querySelector('.push_side[data-id="#js_cart_popup"]');
            if (cartTrigger) setTimeout(function () { cartTrigger.click(); }, 120);
          });

        showToast('Items added to your cart!', 'success');
      })
      .catch(function (err) {
        var msg = err && err.message
          ? err.message
          : 'Something went wrong. Please try again.';
        showToast(msg, 'error');
      })
      .finally(function () {
        setAtcLoading(false);
      });
  }

  function setAtcLoading(isLoading) {
    if (!atcBtn) return;
    if (isLoading) {
      atcBtn.classList.add('is-loading');
      atcBtn.disabled = true;
      if (atcBtnText) atcBtnText.textContent = 'Adding\u2026';
    } else {
      atcBtn.classList.remove('is-loading');
      // Always recalculate disabled state + text from current quantities.
      // On error: quantities are still > 0 → button re-enabled, text restored.
      // On success: quantities were zeroed → button stays disabled, text = "SELECT SIZES ABOVE".
      updateAtcButton();
    }
  }

  /* ─────────────────────────────────────────────────────────── *
   *  SIZE CHART POPUP
   * ─────────────────────────────────────────────────────────── */
  function bindSizeChart() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('#wh-size-chart-btn')) {
        if (scOverlay) scOverlay.classList.add('is-active');
      }
      if (e.target.closest('#wh-sc-close') || e.target === scOverlay) {
        if (scOverlay) scOverlay.classList.remove('is-active');
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && scOverlay && scOverlay.classList.contains('is-active')) {
        scOverlay.classList.remove('is-active');
      }
    });
  }

  /* ─────────────────────────────────────────────────────────── *
   *  NOTIFY ME POPUP
   * ─────────────────────────────────────────────────────────── */
  function openNotifyPopup(locName, size) {
    state.notifyVariantLabel = locName + ' – ' + size;
    if (nmTitle) nmTitle.textContent = 'Notify Me: ' + size + ' at ' + locName;
    if (nmDesc) nmDesc.textContent = 'Enter your email and we\'ll notify you when ' + size + ' becomes available at ' + locName + '.';
    if (nmForm) nmForm.style.display = '';
    if (nmSuccess) nmSuccess.style.display = 'none';
    if (nmEmailInput) nmEmailInput.value = '';
    if (nmOverlay) nmOverlay.classList.add('is-active');
  }

  function bindNotifyPopup() {
    // ── Close on backdrop click or ✕ button ──
    document.addEventListener('click', function (e) {
      if (e.target.closest('#wh-nm-close') || e.target === nmOverlay) {
        if (nmOverlay) nmOverlay.classList.remove('is-active');
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nmOverlay && nmOverlay.classList.contains('is-active')) {
        nmOverlay.classList.remove('is-active');
      }
    });
  }

  /* ─────────────────────────────────────────────────────────── *
   *  STATE MACHINE
   * ─────────────────────────────────────────────────────────── */
  function setState(s) {
    if (!errorEl || !contentEl) return;

    // Card skeleton
    if (loadingEl) loadingEl.classList.remove('is-active');
    // Table skeleton
    if (tmLoadingEl) tmLoadingEl.classList.remove('is-active');
    errorEl.classList.remove('is-active');
    contentEl.classList.remove('is-active');

    if (s === 'loading') {
      if (state.layout === 'table_matrix' || state.layout === 'image_swatch_table' || state.layout === 'image_swatch_table_readonly') {
        if (tmLoadingEl) tmLoadingEl.classList.add('is-active');
      } else {
        if (loadingEl) loadingEl.classList.add('is-active');
      }
    }
    if (s === 'error') errorEl.classList.add('is-active');
    if (s === 'content') contentEl.classList.add('is-active');
  }

  function showError(msg) {
    var p = errorEl ? errorEl.querySelector('.wh-error-msg') : null;
    if (p) p.textContent = msg;
  }

  /* ─────────────────────────────────────────────────────────── *
   *  RETRY BUTTON
   * ─────────────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    if (e.target.closest('#wh-retry-btn')) {
      fetchInventory(state.productId, state.currentColor);
    }
  });

  /* ─────────────────────────────────────────────────────────── *
   *  TOAST
   * ─────────────────────────────────────────────────────────── */
  function createToast() {
    if (document.getElementById('wh-toast')) return;
    toastEl = document.createElement('div');
    toastEl.id = 'wh-toast';
    toastEl.className = 'wh-toast';
    document.body.appendChild(toastEl);
  }

  function showToast(msg, type) {
    if (!toastEl) createToast();
    toastEl.textContent = msg;
    toastEl.className = 'wh-toast wh-toast--' + (type || 'info');
    // Force reflow
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 3500);
  }

  /* ─────────────────────────────────────────────────────────── *
   *  HELPERS
   * ─────────────────────────────────────────────────────────── */
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escAttr(str) { return escHtml(str); }

  /* ─────────────────────────────────────────────────────────── *
   *  RENDER — TABLE MATRIX (new layout)
   * ─────────────────────────────────────────────────────────── */
  function renderTable() {
    if (!contentEl) return;
    preserveReadonlySelectors();

    var td = buildTableData();
    var sizes = td.sizes;
    var priceMap = td.priceMap;
    var locationNames = Object.keys(state.locationMap);

    if (!sizes.length || !locationNames.length) {
      contentEl.innerHTML = '<p style="padding:20px;text-align:center;color:#6b7280;font-size:13px;">No inventory data available.</p>';
      return;
    }

    // Find current color image
    var thumbSrc = '';
    state.colorOptions.forEach(function (o) { if (o.name === state.currentColor) thumbSrc = o.image; });

    var html = '';

    // ── Controls bar: dropdown only (no preview image) ────────────────
    if (state.colorOptions.length > 0) {
      html += '<div class="wh-tm-controls">';
      html += '<div class="wh-tm-dd-wrap">' + buildColorDropdownHTML() + '</div>';
      html += '</div>';
    }

    // ── Selected colour indicator bar ───────────────────────────
    html += '<div class="wh-tm-color-bar">';
    if (thumbSrc) {
      html += '<img class="wh-tm-bar-thumb" id="wh-tm-bar-thumb" src="' + escAttr(thumbSrc) + '" alt="' + escAttr(state.currentColor) + '" width="28" height="28" loading="lazy">';
    }
    html += '<span class="wh-tm-bar-name" id="wh-tm-bar-name">' + escHtml(state.currentColor) + '</span>';
    html += '</div>';

    // ── Table ───────────────────────────────────────────────
    html += '<div class="wh-tm-scroll"><table class="wh-tm-table" role="table">';

    // Header row (no dropdown in corner)
    html += '<thead><tr>';
    html += '<th class="wh-tm-corner" scope="col"><span class="wh-tm-size-label">Size</span><span class="wh-tm-size-price">Price</span></th>';

    sizes.forEach(function (size) {
      html += '<th class="wh-tm-th-size" scope="col">'
        + '<span class="wh-tm-size-label">' + escHtml(size) + '</span>'
        + '<span class="wh-tm-size-price">' + escHtml(priceMap[size] || '') + '</span>'
        + '</th>';
    });
    html += '</tr></thead>';

    // Body rows (one per location)
    html += '<tbody>';
    locationNames.forEach(function (locName) {
      var locItems = (state.locationMap[locName] && state.locationMap[locName].items) || [];

      var hasStock = false;
      locItems.forEach(function (i) { if (i.qty > 0) hasStock = true; });
      if (!hasStock) return;

      html += '<tr class="wh-tm-tr">';
      html += '<td class="wh-tm-loc-cell">'
        + '<span class="wh-tm-loc-name" title="' + escAttr(locName) + '">' + escHtml(locName) + '</span>'
        + '<span class="wh-tm-loc-sub">Warehouse</span>'
        + '</td>';
      sizes.forEach(function (size) {
        var item = null;
        locItems.forEach(function (i) { if (i.size === size) item = i; });
        var soldOut = !item || item.qty <= 0;
        var maxQty = item ? item.qty : 0;
        var numericId = item ? item.numericId : '';
        var qKey = locName + '|||' + size;
        var curQty = state.quantities[qKey] || 0;

        html += '<td class="wh-tm-td' + (soldOut ? ' is-sold-out' : '') + (curQty > 0 ? ' is-active' : '') + '"'
          + ' data-loc="' + escAttr(locName) + '"'
          + ' data-size="' + escAttr(size) + '"'
          + ' data-variant-id="' + escAttr(numericId) + '"'
          + ' data-max="' + maxQty + '"'
          + '>';

        if (state.layout === 'image_swatch_table_readonly') {
          if (soldOut) {
            html += '<span class="wh-tm-readonly-stock is-sold-out">0</span>';
          } else {
            html += '<span class="wh-tm-readonly-stock">' + maxQty.toLocaleString() + '</span>';
          }
        } else {
          if (soldOut) {
            html += '<input type="number" class="wh-tm-input" value="0" disabled aria-label="' + escAttr(size + ' at ' + locName) + '">';
            html += '<span class="wh-tm-out-text">Out Stock</span>';
            html += '<button type="button" class="wh-tm-notify-btn"'
              + ' data-notify-loc="' + escAttr(locName) + '"'
              + ' data-notify-size="' + escAttr(size) + '"'
              + '>Notify</button>';
          } else {
            html += '<input type="number" class="wh-tm-input" value="' + curQty + '" min="0" max="' + maxQty + '" aria-label="' + escAttr(size + ' at ' + locName) + '">';
            html += '<span class="wh-tm-stock">' + maxQty.toLocaleString() + '</span>';
          }
        }

        html += '</td>';
      });

      html += '</tr>';
    });

    html += '</tbody></table></div>';
    contentEl.innerHTML = html;
    if (state.layout === 'image_swatch_table_readonly') {
      relocateReadonlySelectors();
      updateReadonlyStockDisplay();
    }
    updateAtcButton();
    if (state.colorOptions.length > 0) ensureColorDropdownPanel();
  }

  /* ─────────────────────────────────────────────────────────── *
   *  BUILD TABLE DATA (sorted sizes + price map)
   * ─────────────────────────────────────────────────────────── */
  function buildTableData() {
    var SIZE_ORDER = ['2XS', 'XS', 'S', 'M', 'L', 'XL', 'XL-T', '2XL', '2XL-T', '3XL', '4XL', '5XL', '6XL'];
    var sizeSet = {};
    var priceMap = {};

    Object.keys(state.locationMap).forEach(function (locName) {
      var items = (state.locationMap[locName] && state.locationMap[locName].items) || [];
      items.forEach(function (item) {
        sizeSet[item.size] = true;
        if (!priceMap[item.size]) priceMap[item.size] = item.price;
      });
    });

    var sizes = Object.keys(sizeSet);
    sizes.sort(function (a, b) {
      var ai = SIZE_ORDER.indexOf(a);
      var bi = SIZE_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    return { sizes: sizes, priceMap: priceMap };
  }

  /* ─────────────────────────────────────────────────────────── *
   *  TABLE INPUTS (bound once from init)
   * ─────────────────────────────────────────────────────────── */
  function bindTableInputs() {
    // ── Number input: live highlight on input ──
    document.addEventListener('input', function (e) {
      if (!e.target.classList.contains('wh-tm-input')) return;
      e.preventDefault();
      var cell = e.target.closest('.wh-tm-td');
      if (!cell) return;
      var val = parseInt(e.target.value, 10) || 0;
      if (val > 0) { cell.classList.add('is-active'); }
      else { cell.classList.remove('is-active'); }
      var qKey = cell.dataset.loc + '|||' + cell.dataset.size;
      state.quantities[qKey] = val;
      updateAtcButton();
    });

    // ── Number input: validate on blur ──
    document.addEventListener('blur', function (e) {
      if (!e.target.classList.contains('wh-tm-input')) return;
      var cell = e.target.closest('.wh-tm-td');
      if (!cell) return;
      var maxQty = parseInt(cell.dataset.max, 10) || 0;
      var val = parseInt(e.target.value, 10);
      if (isNaN(val) || val < 0) val = 0;
      if (val > maxQty) {
        val = maxQty;
        showCellWarning(cell, 'Max: ' + maxQty);
      }
      e.target.value = val;
      var qKey = cell.dataset.loc + '|||' + cell.dataset.size;
      state.quantities[qKey] = val;
      if (val > 0) { cell.classList.add('is-active'); }
      else { cell.classList.remove('is-active'); }
      updateAtcButton();
    }, true);

    // ── Notify button inside table cell ──
    // Bound on contentEl (not document) so the stepper's stopPropagation
    // on the same element cannot block this listener from running.
    contentEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.wh-tm-notify-btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      openNotifyPopup(btn.dataset.notifyLoc, btn.dataset.notifySize);
    });

    // ── Custom dropdown: open / close toggle ──
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('#wh-color-dd-trigger');
      var panel = document.getElementById('wh-color-dd-panel');
      var ddBtn = document.getElementById('wh-color-dd-trigger');

      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        var isOpen = panel && panel.classList.contains('is-open');
        if (!isOpen) {
          if (panel && ddBtn) {
            var rect = ddBtn.getBoundingClientRect();
            panel.style.top = (rect.bottom + window.scrollY) + 'px';
            panel.style.left = (rect.left + window.scrollX) + 'px';
            panel.style.width = rect.width + 'px';
          }
          if (panel) panel.classList.add('is-open');
          if (ddBtn) { ddBtn.classList.add('is-open'); ddBtn.setAttribute('aria-expanded', 'true'); }
          var si = document.getElementById('wh-color-dd-search');
          if (si) { si.value = ''; si.dispatchEvent(new Event('input')); setTimeout(function () { si.focus(); }, 40); }
        } else {
          if (panel) panel.classList.remove('is-open');
          if (ddBtn) { ddBtn.classList.remove('is-open'); ddBtn.setAttribute('aria-expanded', 'false'); }
        }
        return;
      }

      // Close if clicking outside
      var inPanel = panel && (panel === e.target || panel.contains(e.target));
      var inTriggerWrap = !!e.target.closest('#wh-color-dd');
      if (!inTriggerWrap && !inPanel && panel && panel.classList.contains('is-open')) {
        panel.classList.remove('is-open');
        var ddBtn2 = document.getElementById('wh-color-dd-trigger');
        if (ddBtn2) { ddBtn2.classList.remove('is-open'); ddBtn2.setAttribute('aria-expanded', 'false'); }
      }
    });

    // ── Custom dropdown: option selection ──
    document.addEventListener('click', function (e) {
      var option = e.target.closest('#wh-color-dd-list .wh-color-dd-option');
      if (!option) return;
      e.preventDefault();
      e.stopPropagation();

      var panel = document.getElementById('wh-color-dd-panel');
      var ddBtn = document.getElementById('wh-color-dd-trigger');
      if (panel) panel.classList.remove('is-open');
      if (ddBtn) { ddBtn.classList.remove('is-open'); ddBtn.setAttribute('aria-expanded', 'false'); }

      var newColor = option.dataset.color;
      if (!newColor || newColor === state.currentColor) return;

      updateSwatchActive(newColor);
      syncColorDropdown(newColor);
      state.currentColor = newColor;

      var escapedColor = newColor.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      var swatchInput = document.querySelector(
        'input[type="radio"][name="Color"][value="' + escapedColor + '"],' +
        'input[type="radio"][name="color"][value="' + escapedColor + '"],' +
        'input[type="radio"][name="Colour"][value="' + escapedColor + '"]'
      );
      if (swatchInput) {
        var swatchDiv = swatchInput.closest('.product-options__value');
        if (swatchDiv) swatchDiv.click();
      }
      fetchInventory(state.productId, state.currentColor);
    });

    // ── Custom dropdown: search filter ──
    document.addEventListener('input', function (e) {
      if (e.target.id !== 'wh-color-dd-search') return;
      var q = e.target.value.toLowerCase().trim();
      var opts = document.querySelectorAll('#wh-color-dd-list .wh-color-dd-option');
      opts.forEach(function (o) {
        o.classList.toggle('wh-dd-hidden', q.length > 0 && (o.dataset.color || '').toLowerCase().indexOf(q) === -1);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────── *
   *  REPOSITION DROPDOWN PANEL ON SCROLL / RESIZE
   * ─────────────────────────────────────────────────────────── */
  function repositionColorDd() {
    var panel = document.getElementById('wh-color-dd-panel');
    var ddBtn = document.getElementById('wh-color-dd-trigger');
    if (!panel || !panel.classList.contains('is-open') || !ddBtn) return;
    var rect = ddBtn.getBoundingClientRect();
    panel.style.top = (rect.bottom + window.scrollY) + 'px';
    panel.style.left = (rect.left + window.scrollX) + 'px';
    panel.style.width = rect.width + 'px';
  }
  window.addEventListener('scroll', repositionColorDd, true);
  window.addEventListener('resize', repositionColorDd);

  /* ─────────────────────────────────────────────────────────── *
   *  SYNC COLOR DROPDOWN ↔ THEME SWATCH
   * ─────────────────────────────────────────────────────────── */
  function syncColorDropdown(colorName) {
    // Update trigger label
    var nameEl = document.getElementById('wh-color-dd-name');
    if (nameEl) nameEl.textContent = colorName;

    // Update trigger thumbnail and colour bar
    var matchedOpt = null;
    state.colorOptions.forEach(function (o) { if (o.name === colorName) matchedOpt = o; });

    if (matchedOpt && matchedOpt.image) {
      var thumb = document.getElementById('wh-tm-thumb');
      if (thumb) { thumb.src = matchedOpt.image; thumb.alt = colorName; }

      var barThumb = document.getElementById('wh-tm-bar-thumb');
      if (barThumb) { barThumb.src = matchedOpt.image; barThumb.alt = colorName; }
    }

    var barName = document.getElementById('wh-tm-bar-name');
    if (barName) barName.textContent = colorName;

    // Update selected state in option list
    var opts = document.querySelectorAll('#wh-color-dd-list .wh-color-dd-option');
    opts.forEach(function (o) {
      var sel = o.dataset.color === colorName;
      o.classList.toggle('is-selected', sel);
      o.setAttribute('aria-selected', sel ? 'true' : 'false');
    });
  }

  /* ─────────────────────────────────────────────────────────── *
   *  BUILD CUSTOM COLOR DROPDOWN HTML
   * ─────────────────────────────────────────────────────────── */
  /**
   * Returns ONLY the trigger button HTML.
   * The panel lives permanently on <body> via ensureColorDropdownPanel().
   */
  function buildColorDropdownHTML() {
    var thumbSrc = '';
    state.colorOptions.forEach(function (o) { if (o.name === state.currentColor) thumbSrc = o.image; });

    var html = '<div class="wh-color-dd" id="wh-color-dd">';
    html += '<button type="button" class="wh-color-dd-trigger" id="wh-color-dd-trigger" aria-haspopup="listbox" aria-expanded="false">';
    if (thumbSrc) {
      html += '<img class="wh-color-dd-img" id="wh-tm-thumb" src="' + escAttr(thumbSrc) + '" alt="' + escAttr(state.currentColor) + '" width="24" height="24" loading="lazy">';
    }
    html += '<span class="wh-color-dd-name" id="wh-color-dd-name">' + escHtml(state.currentColor) + '</span>';
    html += '<svg class="wh-color-dd-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>';
    html += '</button>';
    html += '</div>';
    return html;
  }

  /**
   * Creates the dropdown panel on <body> if it doesn't exist yet,
   * then refreshes the options list. Called after every renderTable().
   * This guarantees there is ALWAYS exactly one panel — never duplicated.
   */
  function ensureColorDropdownPanel() {
    var panel = document.getElementById('wh-color-dd-panel');

    if (!panel) {
      panel = document.createElement('div');
      panel.id = 'wh-color-dd-panel';
      panel.className = 'wh-color-dd-panel';
      panel.setAttribute('role', 'listbox');
      panel.setAttribute('aria-label', 'Select colour');

      var searchWrap = document.createElement('div');
      searchWrap.className = 'wh-color-dd-search-wrap';
      var si = document.createElement('input');
      si.type = 'text';
      si.id = 'wh-color-dd-search';
      si.className = 'wh-color-dd-search';
      si.placeholder = 'Search colors…';
      si.autocomplete = 'off';
      searchWrap.appendChild(si);
      panel.appendChild(searchWrap);

      var list = document.createElement('div');
      list.id = 'wh-color-dd-list';
      list.className = 'wh-color-dd-list';
      panel.appendChild(list);

      document.body.appendChild(panel);
    }

    // Always refresh option list to reflect current state.colorOptions & selection
    var list = document.getElementById('wh-color-dd-list');
    if (!list) return;
    var listHtml = '';
    state.colorOptions.forEach(function (opt) {
      var isSelected = opt.name === state.currentColor;
      listHtml += '<button type="button"'
        + ' class="wh-color-dd-option' + (isSelected ? ' is-selected' : '') + '"'
        + ' data-color="' + escAttr(opt.name) + '"'
        + ' role="option"'
        + ' aria-selected="' + (isSelected ? 'true' : 'false') + '">'
        + (opt.image ? '<img class="wh-color-dd-opt-img" src="' + escAttr(opt.image) + '" alt="' + escAttr(opt.name) + '" width="22" height="22" loading="lazy">' : '')
        + '<span class="wh-color-dd-opt-name">' + escHtml(opt.name) + '</span>'
        + '</button>';
    });
    list.innerHTML = listHtml;
  }

  /* ─────────────────────────────────────────────────────────── *
   *  PARSE COLOR OPTIONS (from Liquid JSON embed)
   * ─────────────────────────────────────────────────────────── */
  function parseColorOptions() {
    try {
      var el = document.getElementById('wh-color-data');
      if (!el) return [];
      return JSON.parse(el.textContent || el.innerText) || [];
    } catch (e) {
      return [];
    }
  }

  /* ─────────────────────────────────────────────────────────── *
   *  CELL WARNING (max exceeded)
   * ─────────────────────────────────────────────────────────── */
  function showCellWarning(cell, msg) {
    var existing = cell.querySelector('.wh-tm-warning');
    if (existing) existing.remove();
    var warn = document.createElement('span');
    warn.className = 'wh-tm-warning';
    warn.textContent = msg;
    cell.appendChild(warn);
    setTimeout(function () { if (warn.parentNode) warn.remove(); }, 2000);
  }

  /* ─────────────────────────────────────────────────────────── *
   *  COLOUR IMAGE SWATCH STRIP  (image_swatch_table layout)
   * ─────────────────────────────────────────────────────────── */

  /**
   * Renders (or re-renders) the full swatch strip from state.colorOptions.
   * Called every time render() runs for image_swatch_table layout.
   */
  function renderOutOfStock() {
    if (!contentEl) return;
    preserveReadonlySelectors();
    state.quantities = {};
    updateAtcButton();
    contentEl.innerHTML = '<div class="wh-color-out-stock-msg">' +
      '<p><strong>' + escHtml(state.currentColor) + '</strong> is currently out of stock.</p>' +
      '</div>';
  }

  function bindSwatchHover() {
    var container = document.getElementById('wh-color-swatches');
    if (!container) return;

    container.addEventListener('mouseover', function (e) {
      var btn = e.target.closest('.wh-swatch-item');
      if (btn && colorNameEl) {
        colorNameEl.textContent = btn.dataset.color;
      }
    });

    container.addEventListener('mouseout', function (e) {
      if (colorNameEl) colorNameEl.textContent = state.currentColor;
    });
  }

  function bindStockOnlyCheckbox() {
    if (stockOnlyCheckbox) {
      stockOnlyCheckbox.addEventListener('change', function (e) {
        state.showAvailableOnly = e.target.checked;
        renderColorSwatches();
      });
    }
  }

  /**
   * Renders (or re-renders) the full swatch strip from state.colorOptions.
   * Called every time render() runs for image_swatch_table layout.
   */
  function renderColorSwatches() {
    var container = document.getElementById('wh-color-swatches');
    if (!container) return;

    if (!state.colorOptions.length) {
      return;
    }

    var html = '';
    var visibleCount = 0;
    state.colorOptions.forEach(function (opt) {
      if (state.showAvailableOnly && opt.available === false) return;
      visibleCount++;

      var isActive = opt.name === state.currentColor;
      var outStockHtml = opt.available === false ? '<span class="wh-swatch-out-text">Out Stock</span>' : '';
      var classes = 'wh-swatch-item' + (isActive ? ' is-active' : '') + (opt.available === false ? ' is-out-stock' : '');

      html += '<button type="button"'
        + ' class="' + classes + '"'
        + ' data-color="' + escAttr(opt.name) + '"'
        + ' aria-pressed="' + (isActive ? 'true' : 'false') + '"'
        + ' aria-label="' + escAttr(opt.name) + '"'
        + '>'
        + '<div class="wh-swatch-img-wrap">'
        + '<img class="wh-swatch-img" src="' + escAttr(opt.image) + '" alt="' + escAttr(opt.name) + '" width="64" height="64" loading="lazy">'
        + outStockHtml
        + '</div>'
        + '</button>';
    });
    container.innerHTML = html;

    if (colorCountEl) colorCountEl.textContent = visibleCount;
    if (colorNameEl) colorNameEl.textContent = state.currentColor;
  }

  /**
   * Updates only the active class on existing swatch buttons.
   * Lightweight — does NOT re-render the strip.
   */
  function updateSwatchActive(colorName) {
    var items = document.querySelectorAll('#wh-color-swatches .wh-swatch-item');
    items.forEach(function (btn) {
      var isActive = btn.dataset.color === colorName;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    // Also keep the corner thumbnail in sync
    var matchedOpt = null;
    state.colorOptions.forEach(function (o) { if (o.name === colorName) matchedOpt = o; });
    var thumb = document.getElementById('wh-tm-thumb');
    if (thumb && matchedOpt && matchedOpt.image) {
      thumb.src = matchedOpt.image;
      thumb.alt = colorName;
    }
  }

  /**
   * Delegated click handler for the swatch strip.
   * Bound once from init(). Works for any layout because the
   * container is wh-d-none for non-swatch layouts.
   */
  function bindSwatchClicks() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('#wh-color-swatches .wh-swatch-item');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      var newColor = btn.dataset.color;
      if (!newColor || newColor === state.currentColor) return;

      // Visual feedback first
      updateSwatchActive(newColor);
      syncColorDropdown(newColor);

      // Set state BEFORE clicking swatchDiv so that if the click triggers
      // a radio 'change' event synchronously, bindColorListener's guard
      // (newColor !== state.currentColor) is FALSE — preventing double-fetch
      state.currentColor = newColor;

      // Click the parent .product-options__value div — triggers theme's onProcess():
      // updates main product image, price, URL and Add to Cart button state
      var escapedColor = newColor.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      var radio = document.querySelector(
        'input[type="radio"][name="Color"][value="' + escapedColor + '"],' +
        'input[type="radio"][name="color"][value="' + escapedColor + '"],' +
        'input[type="radio"][name="Colour"][value="' + escapedColor + '"]'
      );
      if (radio) {
        var swatchDiv = radio.closest('.product-options__value');
        // .click() works even if the element is visually hidden (display:none)
        // so this still fires when product_variants swatches are hidden
        if (swatchDiv) swatchDiv.click();
      }

      // Always fetch inventory directly — do NOT rely on radio 'change' event
      fetchInventory(state.productId, state.currentColor);
    });
  }

  /* ─────────────────────────────────────────────────────────── *
   *  READ-ONLY LAYOUT HELPERS
   * ─────────────────────────────────────────────────────────── */
  function preserveReadonlySelectors() {
    var selectors = document.querySelector('.wh-readonly-interactive-selectors');
    if (selectors && contentEl && selectors.parentNode === contentEl) {
      root.appendChild(selectors);
    }
  }

  function relocateReadonlySelectors() {
    if (state.layout !== 'image_swatch_table_readonly') return;
    var selectors = document.querySelector('.wh-readonly-interactive-selectors');
    if (!selectors) return;

    var originalOptions = document.querySelector('#product-single > .form > .product-collection__options, #product-single .product-collection__options');
    if (originalOptions && !selectors.contains(originalOptions)) {
      var placeholder = selectors.querySelector('.product-collection__options');
      if (placeholder) {
        placeholder.parentNode.replaceChild(originalOptions, placeholder);
      } else {
        selectors.insertBefore(originalOptions, selectors.firstChild);
      }
      originalOptions.style.display = 'block';
    }

    var controls = contentEl.querySelector('.wh-tm-controls');
    if (controls) {
      controls.parentNode.insertBefore(selectors, controls.nextSibling);
    } else {
      contentEl.insertBefore(selectors, contentEl.firstChild);
    }
    selectors.style.display = 'block';
  }

  function updateReadonlyStockDisplay(targetVariantId) {
    if (state.layout !== 'image_swatch_table_readonly') return;
    if (!Object.keys(state.locationMap).length) return; // race-condition guard: map not loaded yet

    var input = document.querySelector('[data-js-product-variant-input]');
    var currentVariantId = targetVariantId || (input ? input.value : '');

    // Try to match by variant ID first
    var matchedById = false;
    if (currentVariantId) {
      Object.keys(state.locationMap).forEach(function(locName) {
        (state.locationMap[locName].items || []).forEach(function(item) {
          if (String(item.numericId) === String(currentVariantId)) matchedById = true;
        });
      });
    }

    var totalStock = 0;

    if (matchedById) {
      Object.keys(state.locationMap).forEach(function(locName) {
        (state.locationMap[locName].items || []).forEach(function(item) {
          if (String(item.numericId) === String(currentVariantId)) totalStock += item.qty;
        });
      });
    } else {
      // ID miss = stale event (wrong colour) or theme didn't set :checked on the radio.
      // Read active size from .active class (what this theme actually sets) or label text.
      // ponytail: no :checked selector — theme only sets .active on the wrapper div.
      var activeEl = document.querySelector(
        '.cms-option-item[data-position="2"] .product-options__value.active input'
      );
      var activeSize = activeEl ? activeEl.value : '';
      if (!activeSize) {
        var lbl = document.querySelector('.cms-option-item[data-position="2"] .label-selected');
        if (lbl) activeSize = lbl.textContent.trim();
      }
      if (!activeSize) {
        // last resort: first size in map
        var firstLoc = Object.keys(state.locationMap)[0];
        var firstItem = firstLoc && (state.locationMap[firstLoc].items || [])[0];
        if (firstItem) activeSize = firstItem.size;
      }
      Object.keys(state.locationMap).forEach(function(locName) {
        (state.locationMap[locName].items || []).forEach(function(item) {
          if (item.size === activeSize) totalStock += item.qty;
        });
      });
    }

    var stockValEl = document.getElementById('wh-current-stock-val');
    if (stockValEl) {
      stockValEl.textContent = totalStock.toLocaleString();
    }

    var qtyInput = document.querySelector('.wh-readonly-interactive-selectors input.js_qty');
    if (qtyInput) {
      qtyInput.dataset.max = totalStock;
      qtyInput.setAttribute('max', totalStock);
      var val = parseInt(qtyInput.value, 10) || 1;
      if (val > totalStock) {
        qtyInput.value = totalStock > 0 ? totalStock : 1;
      }
    }

    updateAtcButton();
  }

  function bindVariantChangeListener() {
    document.body.addEventListener('afterVariantUpdated', function (e) {
      var variant = e.detail;
      if (variant && state.layout === 'image_swatch_table_readonly') {
        updateReadonlyStockDisplay(variant.id);
      }
    });

    document.addEventListener('input', function (e) {
      if (e.target && e.target.hasAttribute('data-js-product-variant-input')) {
        if (state.layout === 'image_swatch_table_readonly') {
          updateReadonlyStockDisplay(e.target.value);
        }
      }
    });
  }

  function getProductJson() {
    try {
      var el = document.querySelector(".data-json-product");
      return el ? JSON.parse(el.textContent || el.innerText) : null;
    } catch(e) {
      return null;
    }
  }

  function getVariantById(variantId) {
    if (!variantId) return null;
    var productJson = getProductJson();
    if (productJson && productJson.variants) {
      for (var i = 0; i < productJson.variants.length; i++) {
        if (String(productJson.variants[i].id) === String(variantId)) {
          return productJson.variants[i];
        }
      }
    }
    if (window._whVariants && Array.isArray(window._whVariants)) {
      for (var j = 0; j < window._whVariants.length; j++) {
        var v = window._whVariants[j];
        var numId = v.id ? String(v.id).split('/').pop() : '';
        if (String(numId) === String(variantId) || String(v.id) === String(variantId)) {
          return {
            id: variantId,
            available: (v.inventoryQuantity > 0 || true),
            title: v.title,
            options: v.selectedOptions ? v.selectedOptions.map(function(o){ return o.value; }) : [],
            price: v.price ? Math.round(parseFloat(v.price.amount || v.price) * 100) : 0
          };
        }
      }
    }
    return null;
  }

  /* ─────────────────────────────────────────────────────────── *
   *  BOOT
   * ─────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
